import { useState } from 'react'
import { MinimalAlert, MinimalButton } from '@ovasabi/ui-minimal'
import { promptInstall, useInstallState, type InstallState } from '../../lib/installPrompt'

const DISMISS_KEY = 'chow:install-nudge-dismissed'

const isIphone = () => typeof navigator !== 'undefined' && /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent)

/**
 * The steps for the browser actually in the visitor's hand, in the words its
 * own buttons use. One sentence covering every browser ("tap Share, then Add
 * to Home Screen") was wrong for Android, and for anyone who opened the link
 * inside WhatsApp — where there is no install option at all.
 */
const stepsFor = (state: InstallState): { lead: string; steps: string[] } | null => {
  switch (state) {
    case 'ios':
      return {
        lead: 'Add ChooseChow to your Home Screen in three taps:',
        steps: [
          'Tap the Share button at the bottom of Safari (a square with an arrow pointing up). On iPad it is at the top.',
          'Scroll down and tap “Add to Home Screen”.',
          'Tap “Add”. Open ChooseChow from your Home Screen to get order updates.',
        ],
      }
    case 'ios-browser':
      return {
        lead: 'Add ChooseChow to your Home Screen in three taps:',
        steps: [
          'Tap the Share button next to the address bar (a square with an arrow pointing up).',
          'Tap “Add to Home Screen”. If you can’t see it, tap “More” first.',
          'Tap “Add”. Open ChooseChow from your Home Screen to get order updates.',
        ],
      }
    case 'android':
      return {
        lead: 'Add ChooseChow to your phone in three taps:',
        steps: [
          'Tap the menu (⋮) at the top right of your browser.',
          'Tap “Install app” or “Add to Home screen”.',
          'Tap “Install”. ChooseChow will sit with your other apps.',
        ],
      }
    case 'in-app':
      return {
        lead: 'You’re in a chat app’s browser, which can’t install apps. Open ChooseChow in your phone’s browser first:',
        steps: [
          'Tap ⋮ or ⋯ at the top or bottom corner of this screen.',
          isIphone() ? 'Tap “Open in Safari”.' : 'Tap “Open in Chrome” or “Open in browser”.',
          'Come back to this page there and you’ll see how to install.',
        ],
      }
    default:
      return null
  }
}

const InstallSteps = ({ state }: { state: InstallState }) => {
  const guide = stepsFor(state)
  if (!guide) return null
  return (
    <div>
      <p style={{ margin: 0 }}>{guide.lead}</p>
      <ol style={{ margin: '6px 0 0', paddingLeft: 20, display: 'grid', gap: 4 }}>
        {guide.steps.map((step) => <li key={step}>{step}</li>)}
      </ol>
    </div>
  )
}

/** Install action and instructions; renders nothing once installed or where the browser cannot install. */
export function InstallAppAction() {
  const state = useInstallState()
  const [error, setError] = useState('')
  if (state === 'installed') return <p>ChooseChow is installed on this device.</p>
  if (state === 'unavailable') return <p>Open ChooseChow on your phone, in Chrome or Safari, to add it to your Home Screen.</p>
  if (state !== 'available') return <InstallSteps state={state} />
  const install = async () => {
    setError('')
    try { await promptInstall() } catch { setError('Your browser didn’t open the install box. Tap its menu (⋮) and choose “Install app”.') }
  }
  return <>
    <div><MinimalButton onClick={() => void install()}>Install ChooseChow</MinimalButton></div>
    {error && <MinimalAlert tone="danger" role="alert">{error}</MinimalAlert>}
  </>
}

/**
 * A dismissible prompt to add ChooseChow to the Home Screen. Installing gives a
 * full-screen app icon and, on iPhone, is required before notifications work.
 * The steps stay folded behind "Show me how" so the nudge is one line until the
 * visitor asks.
 */
export function InstallAppNudge() {
  const state = useInstallState()
  const [open, setOpen] = useState(false)
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem(DISMISS_KEY) === '1' } catch { return false } })
  if (dismissed || state === 'installed' || state === 'unavailable') return null
  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1') } catch { /* hide for this visit */ }
    setDismissed(true)
  }
  return (
    <MinimalAlert
      tone="info"
      title="Get the ChooseChow app"
      action={<>
        {state === 'available'
          ? <MinimalButton size="sm" onClick={() => void promptInstall().catch(() => false)}>Install</MinimalButton>
          : <MinimalButton size="sm" onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? 'Hide steps' : 'Show me how'}</MinimalButton>}
        <MinimalButton size="sm" variant="ghost" onClick={dismiss}>Not now</MinimalButton>
      </>}
    >
      {open && state !== 'available'
        ? <InstallSteps state={state} />
        : 'It opens like a normal app from your Home Screen and tells you when your order moves. Free, and no app store needed.'}
    </MinimalAlert>
  )
}
